'use client';

import React, { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { twJoin, twMerge } from 'tailwind-merge';
import {
  ImageContentFragment,
  ImageFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { VideoSource } from '@/utils/video-source';
import { useHoverCapable } from '@/ui/hooks/use-hover-capable';
import { VideoControls, VideoControlsAnchor } from './video-player-controls';
import {
  VideoInteractionLayer,
  VideoMediaElement,
  VideoOverlay,
  VideoPosterLayer,
} from './video-player-layers';
import { useVideoPlayerState } from './use-video-player-state';
import { VideoPlayerMode } from './use-video-playback';

// The browser sizes a fullscreen element to the screen, overriding the aspect-ratio class, so the
// media is letterboxed on black there instead of being cropped to the screen's ratio.
const WRAPPER_CLASSES = twJoin(
  'group relative overflow-hidden',
  '[&:fullscreen]:flex [&:fullscreen]:items-center [&:fullscreen]:bg-black',
  '[&:fullscreen_video]:object-contain [&:fullscreen_img]:object-contain',
);

export interface VideoPlayerProps {
  source: VideoSource;
  poster?: ImageFragment | null;
  posterComponent?: ImageContentFragment | null;
  /** `sizes` for the poster image; defaults to the half-viewport box the 50:50 tile renders. */
  posterSizes?: string;
  ariaLabel: string;
  mode: VideoPlayerMode;
  aspectRatioClass?: string;
  loop?: boolean;
  isActive?: boolean;
  onPlayingChange?: (playing: boolean) => void;
  /** Fires once the media failed for good and only the poster is left on screen. */
  onFatalError?: () => void;
  /** Fires once the media played through to its end, unless it loops. */
  onEnded?: () => void;
  showControls?: boolean;
  /** The corner the control bar sits in, for tiles whose overlay would cover the default one. */
  controlsAnchor?: VideoControlsAnchor;
  className?: string;
  trackingId?: string;
  children?: React.ReactNode;
}

export function VideoPlayer({
  source,
  poster,
  posterComponent,
  posterSizes,
  ariaLabel,
  mode,
  aspectRatioClass,
  loop,
  isActive,
  onPlayingChange,
  onFatalError,
  onEnded,
  showControls = true,
  controlsAnchor,
  className,
  trackingId,
  children,
}: Readonly<VideoPlayerProps>) {
  const t = useTranslations('Video');
  const isHoverCapable = useHoverCapable();
  const player = useVideoPlayerState({
    mode,
    isActive,
    loop,
    onPlayingChange,
    onEnded,
  });
  const { wrapperRef, onPlayerReady, isPlayerMounted } = player;
  const { playback, media, fullscreen } = player;

  // After a fatal media error only the poster remains, so the play affordance is dropped too.
  const isInteractive = mode !== 'background' && !media.hasFatalError;

  // Tiles that render their own controls around the player have to drop them along with it.
  useEffect(() => {
    if (media.hasFatalError) {
      onFatalError?.();
    }
  }, [media.hasFatalError, onFatalError]);

  // Without hover nothing could ever reveal a faded-out control bar or overlay, so on such a
  // device both stay on screen while the video plays. The first render does not know the device
  // yet and assumes hover, like the tiles do.
  const isOverlayAlwaysVisible = isHoverCapable === false;

  // A player that lost the active slot keeps its media element mounted so playback can resume
  // without re-fetching the stream, but its controls would be a second live-looking bar the user
  // cannot operate. It falls back to the same play affordance an unplayed player offers.
  const isLive = isActive !== false;

  return (
    <div
      ref={wrapperRef}
      role="group"
      aria-label={ariaLabel}
      className={twMerge(WRAPPER_CLASSES, aspectRatioClass, className)}
      onMouseEnter={playback.handleMouseEnter}
      onMouseLeave={playback.handleMouseLeave}
    >
      {/* A background player sits on the tile's own poster, so a second poster layer would only
          fetch the same still once more at another size. */}
      {mode !== 'background' && (
        <VideoPosterLayer
          poster={poster ?? source.thumbnail}
          posterComponent={posterComponent}
          isHidden={media.hasStarted}
          sizes={posterSizes}
        />
      )}
      {isPlayerMounted && (
        <VideoMediaElement
          onReady={onPlayerReady}
          src={source.url}
          playing={playback.playing}
          muted={playback.muted}
          loop={loop}
          events={media}
        />
      )}
      <VideoInteractionLayer
        isPlayerMounted={isPlayerMounted && isLive}
        isInteractive={isInteractive}
        overlayAlwaysVisible={isOverlayAlwaysVisible}
        playing={playback.playing}
        playLabel={t('play')}
        pauseLabel={t('pause')}
        onToggle={playback.togglePlay}
        trackingId={trackingId}
      />
      {/* The browser stretches a fullscreen wrapper to the screen and letterboxes the media inside
          it, so an overlay laid out against the wrapper would float in the black bars next to the
          video. Fullscreen is for watching; the editorial block returns with the tile. */}
      {children && !fullscreen.isFullscreen && (
        <VideoOverlay isVisible={!playback.playing || isOverlayAlwaysVisible}>
          {children}
        </VideoOverlay>
      )}
      {showControls && isInteractive && isPlayerMounted && isLive && (
        <VideoControls
          playing={playback.playing}
          muted={playback.muted}
          showFullscreen={fullscreen.isSupported}
          isFullscreen={fullscreen.isFullscreen}
          isAlwaysVisible={isOverlayAlwaysVisible}
          controlsAnchor={controlsAnchor}
          onTogglePlay={playback.togglePlay}
          onToggleMute={playback.toggleMute}
          onToggleFullscreen={fullscreen.toggle}
          trackingId={trackingId}
        />
      )}
    </div>
  );
}
